import type { TeacherPortfolioTaskResource } from "api/generated";
import type {
  TeacherPortfolioTask,
  TeacherPortfolioTaskMeta,
} from "portfolio/types";
import { mapAttachment, mapShallowTeacher } from "shared/mappers";
import { mapLinks } from "shared/mappers/link.mapper";
import { mapVideos } from "shared/mappers/video.mapper";
import { teacherSkillFromDto } from "./skill.mapper";
import { teacherTagFromDto } from "./tag.mapper";
import { taskQuestionFromDto } from "./taskQuestion.mapper";

const mapTeacherPortfolioTaskMeta = (
  meta: TeacherPortfolioTaskResource["meta"],
): TeacherPortfolioTaskMeta => ({
  canUpdateAssessmentModels: meta.can_update_assessment_models,
  canUpdate: meta.can_update,
  canDelete: meta.can_delete,
  canCreateTemplate: meta.can_create_template ?? false,
  totalStudents: meta.total_students,
  completedTaskAmount: meta.completed_task_amount,
  completedTaskPercentage: meta.completed_task_percentage,
});

export const teacherPortfolioTaskFromDto = (
  resource: TeacherPortfolioTaskResource,
): TeacherPortfolioTask => ({
  id: resource.id,
  publicPortfolioTaskId: resource.public_portfolio_task_id ?? null,
  user: resource.user ? mapShallowTeacher(resource.user) : null,
  type: resource.type,
  teacherShortage: resource.teacher_shortage ?? null,
  title: resource.title,
  description: resource.description,
  teacherInstructions: resource.teacher_instructions ?? null,
  bannerImage: resource.banner_image ?? null,
  status: resource.status,
  studentsSelectedBy: resource.students_selected_by,
  startDate: resource.start_date ? new Date(resource.start_date) : null,
  deadline: resource.deadline ? new Date(resource.deadline) : null,
  allowSendReminderBeforeDeadline: resource.allow_send_reminder_before_deadline,
  sendInvites: resource.send_invites ?? true,
  invitesSentDate: resource.invites_sent_date,
  isRequired: resource.is_required,
  attachmentRequired: resource.attachment_required,
  allowTaskRatings: resource.allow_task_ratings,
  autoAddMentorsAsOwners: resource.auto_add_mentors_as_owners,
  assessmentModels: Array.isArray(resource.assessment_models)
    ? (resource.assessment_models[0] ?? null)
    : (resource.assessment_models ?? null),
  educationLevels: resource.education_levels,
  mentorClasses: resource.mentor_classes,
  lessonGroups: resource.lesson_groups,
  studentIds: resource.student_ids ?? [],
  tags: resource.tags.map(teacherTagFromDto),
  lobCompetences: resource.lob_competences.map((competence) => ({
    id: competence.id,
    name: competence.name,
  })),
  skills: resource.skills.map(teacherSkillFromDto),
  attachments: resource.attachments.map(mapAttachment),
  links: mapLinks(resource.links),
  videos: mapVideos(resource.videos),
  questions: resource.questions.map(taskQuestionFromDto),
  owners: resource.owners.map(mapShallowTeacher),
  createdAt: resource.created_at ? new Date(resource.created_at) : null,
  updatedAt: resource.updated_at ? new Date(resource.updated_at) : null,
  meta: mapTeacherPortfolioTaskMeta(resource.meta),
});
